import {
  GovernanceSection,
  GovernanceSectionProps,
  InvestorsSection,
  InvestorsSectionProps,
  TeamCardsSection,
  TeamCardsSectionProps,
} from '@rimactechnology/rimac-ui-lib';

import {
  FooterQuery,
  GovernanceFragment,
  HeroFragment,
  InvestorsSectionFragment,
  NavigationQuery,
  ProductImageBlockFragment,
  SeoFragment,
  TeamFragment,
} from '@/api';
import { Layout } from '@/components/layout/Layout';
import { Seo } from '@/components/Seo';
import EnhancedProductImageBlock from '@/components/shared/EnhancedProductImageBlock';
import { Hero } from '@/components/shared/Hero';
import getCdlPublicId from '@/hooks/useCdlPublicId';

type Props = {
  seo?: SeoFragment | null;
  hero?: HeroFragment | null;
  governance?: GovernanceFragment | null;
  investors?: InvestorsSectionFragment | null;
  team?: TeamFragment | null;
  productImageBlock?: ProductImageBlockFragment | null;
  header: NavigationQuery;
  footer: FooterQuery;
};

export const GovPage = ({
  seo,
  hero,
  governance,
  investors,
  team,
  productImageBlock,
  header,
  footer,
}: Props) => {
  const governanceSectionProps: GovernanceSectionProps = {
    title: governance?.title || '',
    image: {
      imgSrc: governance?.image?.img_desktop.data?.attributes?.url || '',
      imgSrcMobile: governance?.image?.img_mobile?.data?.attributes?.url || '',
      alt: governance?.image?.alt || '',
      blurHash:
        governance?.image?.img_desktop.data?.attributes?.blurhash ||
        'LAS?DV%M_3D%t7fQofj[~qM{4n-;',
    },
    dropdownItems: (governance?.mobileDropDown || []).map((card, index) => ({
      parentIndex: index || 1,
      title: card?.title || '',
      subItems: (card?.drop_down_item?.data?.attributes?.item || []).map(
        (item) => item?.label || '',
      ),
    })),
    description: governance?.text || '',
    anchor: governance?.anchor || '',
    button: governance?.button
      ? { useCase: 'group', ...governance.button }
      : undefined,
  };

  const investorsSectionProps: InvestorsSectionProps = {
    title: investors?.title || '',
    subtitle: investors?.subtitle || '',
    investors: (investors?.investor || []).map((investor, index) => ({
      index,
      title: investor?.name || '',
      percentage: Number(investor?.percentage || 0),
      bgColor: `#${investor?.bgColor?.split('_').pop()}`,
      text: investor?.note || '',
    })),
    partnerLogos: (investors?.icon || []).map((logo) => ({
      imgSrc: getCdlPublicId(logo?.svg?.data?.attributes?.url || ''),
      alt: logo?.alt || '',
    })),
    anchor: investors?.anchor || '',
  };

  const teamCardsSectionProps: TeamCardsSectionProps = {
    title: team?.title || '',
    anchor: team?.anchor || undefined,
    teammates: (team?.teammates || []).map((person, index) => ({
      parentIndex: index || 1,
      name: person?.name || '',
      position: person?.position || '',
      secondPosition: person?.secondPosition || '',
      logos: (person?.logos?.data || []).map((logo) => ({
        image: {
          imgSrc: getCdlPublicId(logo?.attributes?.url || ''),
          alt: logo?.attributes?.alternativeText || '',
        },
      })),
      image: {
        imgSrc: getCdlPublicId(
          person?.img_desktop?.data?.attributes?.url || '',
        ),
        imgSrcMobile: getCdlPublicId(
          person?.img_mobile?.data?.attributes?.url || undefined,
        ),
        alt: person?.alt || person?.name || '',
        blurHash: person?.img_desktop?.data?.attributes?.blurhash || '12312312',
      },
    })),
  };

  return (
    <Layout headerData={header} footerData={footer}>
      <Seo seo={seo} />
      <Hero hero={hero} titleMaxWidth={720} />
      <TeamCardsSection {...teamCardsSectionProps} />
      <InvestorsSection {...investorsSectionProps} />
      <GovernanceSection {...governanceSectionProps} />
      <EnhancedProductImageBlock
        productImageBlock={productImageBlock}
        parentIndex={1}
      />
    </Layout>
  );
};
